import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { authService } from '@/services/auth.service';
import { venueReservationsService } from '@/services/venue-reservations.service';
import { Button, useToast } from '@caruso/ui';
import {
  Users,
  Tags,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  User,
  Building,
  MessageSquare,
  MessageCircle,
  Mail,
  Settings,
  List,
  LayoutDashboard,
  Calendar,
  GitBranch,
  Package,
  PlayCircle,
  Tag,
  ShoppingBag,
  DoorOpen,
  UserCog,
  Briefcase,
  Utensils,
  ClipboardList,
  Badge,
  Building2,
  CalendarCheck,
  FileText,
  LayoutGrid,
  Headphones,
  Palette,
  Link,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@caruso/ui';
import { ReservationStatus } from '@caruso/types';
import { NotificationDropdown } from '@/components/notifications/notification-dropdown';
import { DevEnvironmentBanner, isDevelopment } from '@/components/dev-environment-banner';

interface MenuItem {
  path?: string;
  label: string;
  icon: any;
  submenu?: MenuItem[];
  badgeKey?: string; // Chave para identificar qual badge mostrar
}

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, logout } = useAuthStore();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(
    new Set(),
  );

  // Query para buscar contagem de reservas pendentes
  const { data: pendingReservations } = useQuery({
    queryKey: ['pending-reservations-count'],
    queryFn: () => venueReservationsService.getAll({
      status: 'pending' as ReservationStatus,
      limit: '1',
    }),
    refetchInterval: 60000, // Atualiza a cada 1 minuto
  });

  const pendingReservationsCount = pendingReservations?.meta?.total || 0;

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      toast({
        title: 'Logout realizado',
        description: 'Até logo!',
        variant: 'success',
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      logout();
      navigate('/login');
    }
  };

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  // Filtrar itens do menu baseado na role do usuário
  const baseMenuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      submenu: [
        { path: '/app/dashboard', label: 'Visão Geral', icon: LayoutDashboard },
        { path: '/app/dashboard/atividades', label: 'Atividades do Dia', icon: ClipboardList },
      ],
    },
    // {
    //   path: '/app/meus-processos',
    //   label: 'Minhas Atividades',
    //   icon: ClipboardList,
    // },
    {
      label: 'Clientes',
      icon: Users,
      submenu: [
        { path: '/app/clientes', label: 'Listagem', icon: Users },
        { path: '/app/empresas', label: 'Empresas', icon: Building },
        { path: '/app/categorias-clientes', label: 'Categorias', icon: Tag },
        { path: '/app/tags', label: 'Tags', icon: Tags },
        { path: '/app/listas', label: 'Listas', icon: List },
      ],
    },
    {
      label: 'Eventos',
      icon: Calendar,
      submenu: [
        { path: '/app/eventos', label: 'Listagem', icon: Calendar },
        { path: '/app/eventos/controle', label: 'Controle de Entrada', icon: DoorOpen },
      ],
    },
    {
      label: 'Fluxos',
      icon: GitBranch,
      submenu: [
        { path: '/app/fluxos', label: 'Listagem', icon: GitBranch },
        { path: '/app/fluxos/execucoes', label: 'Em Execucao', icon: PlayCircle },
        { path: '/app/quadros', label: 'Quadros', icon: LayoutGrid },
        { path: '/app/fluxos/formularios', label: 'Respostas Formularios', icon: FileText },
        { path: '/app/whatsapp', label: 'Conf. WhatsApp', icon: MessageSquare },
        { path: '/app/email-templates', label: 'Temp. de Email', icon: Mail },
      ],
    },
    {
      label: 'Produtos',
      icon: Package,
      submenu: [
        { path: '/app/produtos', label: 'Listagem', icon: Package },
        { path: '/app/produtos/categorias', label: 'Categorias', icon: Tag },
      ],
    },
    {
      label: 'Pedidos',
      icon: ShoppingBag,
      submenu: [
        { path: '/app/pedidos', label: 'Pedidos Externos', icon: ShoppingBag },
        { path: '/checkin', label: 'Check-in Clientes', icon: DoorOpen },
        { path: '/app/mesas', label: 'Mesas', icon: Utensils },
        { path: '/app/comandas', label: 'Comandas', icon: ClipboardList },
      ],
    },
    // {
    //   label: 'Funcionários',
    //   icon: UserCog,
    //   submenu: [
    //     { path: '/app/funcionarios', label: 'Cadastro', icon: UserCog },
    //     { path: '/app/cargos', label: 'Cargos', icon: Badge },
    //     { path: '/app/processos', label: 'Processos', icon: Briefcase },
    //   ],
    // },
    {
      path: '/app/webchat',
      label: 'Atendimento',
      icon: Headphones,
    },
    {
      label: 'Ambientes',
      icon: Building2,
      badgeKey: 'pendingReservations',
      submenu: [
        { path: '/app/venues', label: 'Listagem', icon: Building2 },
        { path: '/app/venue-reservations', label: 'Reservas', icon: CalendarCheck, badgeKey: 'pendingReservations' },
        { path: '/app/venue-reservations/calendar', label: 'Calendário', icon: Calendar },
      ],
    },
    {
      path: '/app/agenda',
      label: 'Minha Agenda',
      icon: CalendarCheck,
    },
    {
      label: 'Configurações',
      icon: Settings,
      submenu: [
        { path: '/app/settings', label: 'Geral', icon: Settings },
        { path: '/app/settings/cores', label: 'Cores do Tema', icon: Palette },
        { path: '/app/settings/integracoes', label: 'Integrações', icon: Link },
      ],
    },
  ];

  // Adicionar menu de usuários e grupos apenas para ADMIN
  const menuItems: MenuItem[] = user?.role === 'ADMIN'
    ? [
        ...baseMenuItems,
        {
          label: 'Sistema',
          icon: Settings,
          submenu: [
            { path: '/app/usuarios', label: 'Usuários', icon: UserCog },
            { path: '/app/grupos', label: 'Grupos', icon: Users },
            { path: '/app/departamentos', label: 'Departamentos', icon: Building2 },
          ],
        },
      ]
    : baseMenuItems;

  // Auto-expand menu when a submenu item is active
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.submenu) {
        const hasActiveSubmenu = item.submenu.some(
          (sub) =>
            sub.path &&
            (location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')),
        );
        if (hasActiveSubmenu) {
          setExpandedMenus((prev) => {
            const newSet = new Set(prev);
            newSet.add(item.label.toLowerCase());
            return newSet;
          });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const getBreadcrumb = () => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);

    if (parts.length === 1) return 'Dashboard';
    if (parts[1] === 'dashboard') {
      if (path.includes('/atividades')) return 'Dashboard / Atividades do Dia';
      return 'Dashboard / Visão Geral';
    }
    if (parts[1] === 'clientes') {
      if (path.includes('/novo')) return 'Clientes / Novo';
      if (parts.length === 3) return 'Clientes / Editar';
      return 'Clientes / Listagem';
    }
    if (parts[1] === 'categorias-clientes') return 'Clientes / Categorias';
    if (parts[1] === 'empresas') {
      if (path.includes('/nova')) return 'Clientes / Empresas / Nova';
      if (parts.length === 3) return 'Clientes / Empresas / Editar';
      return 'Clientes / Empresas';
    }
    if (parts[1] === 'tags') return 'Clientes / Tags';
    if (parts[1] === 'listas') {
      if (path.includes('/nova')) return 'Clientes / Listas / Nova';
      if (parts.length === 3) return 'Clientes / Listas / Editar';
      return 'Clientes / Listas';
    }
    if (parts[1] === 'eventos') {
      if (path.includes('/novo')) return 'Eventos / Novo';
      if (path.includes('/convidados')) return 'Eventos / Convidados';
      if (path.includes('/controle')) return 'Eventos / Controle de Entrada';
      if (parts.length === 3) return 'Eventos / Editar';
      return 'Eventos / Listagem';
    }
    if (parts[1] === 'fluxos') {
      if (path.includes('/execucoes')) return 'Fluxos / Em Execução';
      if (path.includes('/novo')) return 'Fluxos / Novo';
      if (path.includes('/editar')) return 'Fluxos / Editar';
      if (path.includes('/executar')) return 'Fluxos / Executar';
      return 'Fluxos / Listagem';
    }
    if (parts[1] === 'produtos') {
      if (path.includes('/categorias')) return 'Produtos / Categorias';
      if (path.includes('/novo')) return 'Produtos / Novo';
      if (parts.length === 3) return 'Produtos / Editar';
      return 'Produtos / Listagem';
    }
    if (parts[1] === 'pedidos') {
      if (path.includes('/loja')) return 'Pedidos / Loja';
      if (path.includes('/mesas')) return 'Pedidos / Mesas';
      return 'Pedidos / Internos';
    }
    if (parts[1] === 'funcionarios') {
      return 'Funcionários / Cadastro';
    }
    if (parts[1] === 'meus-processos') {
      return 'Minhas Atividades';
    }
    if (parts[1] === 'cargos') {
      if (path.includes('/novo')) return 'Funcionários / Cargos / Novo';
      if (path.includes('/editar') || parts[2]) return 'Funcionários / Cargos / Editar';
      return 'Funcionários / Cargos';
    }
    if (parts[1] === 'processos') {
      if (path.includes('/novo')) return 'Funcionários / Processos / Novo';
      if (path.includes('/editar')) return 'Funcionários / Processos / Editar';
      return 'Funcionários / Processos';
    }
    if (parts[1] === 'whatsapp') {
      if (path.includes('/config')) return 'Fluxos / WhatsApp / Configuração';
      if (path.includes('/test')) return 'Fluxos / WhatsApp / Teste';
      return 'Fluxos / Conf. WhatsApp';
    }
    if (parts[1] === 'email-templates') {
      if (parts.length === 3) return 'Fluxos / Templates Email / Editar';
      return 'Fluxos / Temp. de Email';
    }
    if (parts[1] === 'venues') {
      if (path.includes('/new')) return 'Ambientes / Novo';
      if (parts.length === 3) return 'Ambientes / Editar';
      return 'Ambientes / Listagem';
    }
    if (parts[1] === 'venue-reservations') {
      if (path.includes('/calendar')) return 'Ambientes / Calendário';
      if (path.includes('/new')) return 'Ambientes / Nova Reserva';
      if (parts.length === 3) return 'Ambientes / Editar Reserva';
      return 'Ambientes / Reservas';
    }
    if (parts[1] === 'agenda') return 'Minha Agenda';
    if (parts[1] === 'usuarios') {
      if (path.includes('/novo')) return 'Sistema / Usuários / Novo';
      if (parts.length === 3) return 'Sistema / Usuários / Editar';
      return 'Sistema / Usuários';
    }
    if (parts[1] === 'grupos') {
      if (path.includes('/novo')) return 'Sistema / Grupos / Novo';
      if (parts.length === 3) return 'Sistema / Grupos / Editar';
      return 'Sistema / Grupos';
    }
    if (parts[1] === 'perfil') return 'Meu Perfil';
    if (parts[1] === 'settings') {
      if (path.includes('/cores')) return 'Configurações / Cores do Tema';
      if (path.includes('/integracoes')) return 'Configurações / Integrações';
      return 'Configurações / Geral';
    }
    if (parts[1] === 'webchat') return 'Atendimento / WebChat';
    if (parts[1] === 'departamentos') {
      if (path.includes('/usuarios')) return 'Sistema / Departamentos / Usuários';
      if (parts.length === 3) return 'Sistema / Departamentos / Editar';
      return 'Sistema / Departamentos';
    }
    return 'Dashboard';
  };

  const renderMenuItem = (item: MenuItem) => {
    const Icon = item.icon;
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isExpanded = expandedMenus.has(item.label.toLowerCase());
    const menuId = item.label.toLowerCase();

    // Check if this menu or any of its submenu items is active
    const isActive = item.path
      ? location.pathname === item.path || location.pathname.startsWith(item.path + '/')
      : hasSubmenu &&
        item.submenu!.some((sub) => {
          if (!sub.path) return false;

          // Exact match or starts with path followed by /
          const exactMatch = location.pathname === sub.path;
          const prefixMatch = location.pathname.startsWith(sub.path + '/');

          return exactMatch || prefixMatch;
        });

    // Determina se deve mostrar badge no menu pai
    const parentBadgeCount = item.badgeKey === 'pendingReservations' ? pendingReservationsCount : 0;

    if (hasSubmenu) {
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleMenu(menuId)}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg transition-all w-full group',
              'hover:bg-brand-gold/10',
              isActive ? 'text-brand-gold' : 'text-brand-ivory/70 hover:text-brand-gold',
            )}
          >
            <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
            <span className="font-medium flex-1 text-left">{item.label}</span>
            {parentBadgeCount > 0 && (
              <span className="bg-yellow-500 text-yellow-950 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {parentBadgeCount > 99 ? '99+' : parentBadgeCount}
              </span>
            )}
            <ChevronDown
              className={cn(
                'w-4 h-4 transition-transform flex-shrink-0',
                isExpanded ? 'rotate-180' : '',
              )}
            />
          </button>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-brand-gold/20 pl-2">
                  {item.submenu!.map((subitem) => {
                    const SubIcon = subitem.icon;

                    if (!subitem.path) {
                      return null;
                    }

                    // Verifica se o path atual corresponde exatamente ao subitem
                    const exactMatch = location.pathname === subitem.path;

                    // Verifica se o path atual começa com o path do subitem seguido de /
                    const prefixMatch = location.pathname.startsWith(subitem.path + '/');

                    // Verifica se há outro subitem mais específico (path mais longo) que também corresponde
                    const hasMoreSpecificMatch = item.submenu!.some(
                      (otherSubitem) => {
                        if (!otherSubitem.path || otherSubitem.path === subitem.path) {
                          return false;
                        }
                        // Verifica se o outro subitem é mais específico (path mais longo)
                        // e se o path atual corresponde a ele
                        const otherIsMoreSpecific = otherSubitem.path.length > subitem.path!.length;
                        const currentMatchesOther =
                          location.pathname === otherSubitem.path ||
                          location.pathname.startsWith(otherSubitem.path + '/');
                        return otherIsMoreSpecific && currentMatchesOther;
                      },
                    );

                    // Um subitem está ativo se:
                    // 1. É match exato, OU
                    // 2. É match por prefixo E não há outro subitem mais específico que também corresponde
                    const isSubActive = exactMatch || (prefixMatch && !hasMoreSpecificMatch);

                    // Determina se deve mostrar badge e qual valor
                    const badgeCount = subitem.badgeKey === 'pendingReservations' ? pendingReservationsCount : 0;

                    return (
                      <NavLink
                        key={subitem.path}
                        to={subitem.path!}
                        onClick={() => setIsSidebarOpen(false)}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2 rounded-lg transition-all group text-sm',
                          'hover:bg-brand-gold/10',
                          isSubActive
                            ? 'bg-brand-gold/20 text-brand-gold'
                            : 'text-brand-ivory/60 hover:text-brand-gold',
                        )}
                      >
                        <SubIcon className="w-4 h-4" strokeWidth={1.5} />
                        <span className="font-medium flex-1">{subitem.label}</span>
                        {badgeCount > 0 && (
                          <span className="bg-yellow-500 text-yellow-950 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                        {isSubActive && !badgeCount && <ChevronRight className="w-3 h-3 ml-auto" />}
                      </NavLink>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path!}
        onClick={() => setIsSidebarOpen(false)}
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg transition-all group',
          'hover:bg-brand-gold/10',
          isActive
            ? 'bg-brand-gold/20 text-brand-gold'
            : 'text-brand-ivory/70 hover:text-brand-gold',
        )}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        <span className="font-medium">{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-brand-smoke">
      {/* Development Environment Banner */}
      <DevEnvironmentBanner />

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 h-full w-64 bg-brand-ink border-r border-brand-gold/10 z-50',
          'md:fixed md:block',
          'transition-transform duration-300',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          isDevelopment ? 'top-8' : 'top-0',
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-brand-gold/10">
            <h1 className="text-2xl font-serif text-brand-gold">Caruso CRM</h1>
            <p className="text-xs text-brand-ivory/50 mt-1">Premium Management</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => renderMenuItem(item))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-brand-gold/10">
            <button
              onClick={() => {
                navigate('/app/perfil');
                setIsSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-brand-smoke/30 hover:bg-brand-gold/10 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-brand-gold/20 flex items-center justify-center">
                <User className="w-4 h-4 text-brand-gold" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-brand-ivory truncate">{user?.name}</p>
                <p className="text-xs text-brand-ivory/50 truncate">{user?.email}</p>
              </div>
            </button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full mt-2 justify-start text-brand-ivory/70 hover:text-red-400 hover:bg-red-400/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn(
        "md:ml-64 flex flex-col h-screen",
        isDevelopment && "pt-8"
      )}>
        {/* Header */}
        <header className="bg-brand-ink border-b border-brand-cigar/20 premium-border">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Mobile Menu Toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden text-brand-gold"
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                >
                  {isSidebarOpen ? <X /> : <Menu />}
                </Button>

                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm overflow-hidden">
                  <span className="text-brand-ivory/50 hidden sm:inline">Dashboard</span>
                  <ChevronRight className="w-4 h-4 text-brand-gold/50 hidden sm:inline" />
                  <span className="text-brand-gold font-medium truncate">{getBreadcrumb()}</span>
                </div>
              </div>

              {/* Header Actions */}
              <div className="flex items-center gap-3">
                <NotificationDropdown />
                <span className="text-xs text-brand-ivory/50 hidden sm:inline">
                  {user?.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-gradient-to-br from-brand-smoke via-brand-ink/50 to-brand-smoke">
          <div className="p-4 sm:p-6 lg:p-8">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Outlet />
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
}
